import { Injectable } from '@angular/core';
import { HttpContext, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { updateRequestStatus } from '@ngneat/elf-requests';
import {catchError, filter, map, switchMap, take, tap, timeout, mergeMap, shareReplay, distinctUntilChanged} from 'rxjs/operators';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import {
	upsertEntities,
	setActiveId,
	setEntities,
	resetActiveId,
	deleteAllEntities,
	hasEntity, getActiveId, getEntity
} from '@ngneat/elf-entities';

import { AuthService } from '~core/services/auth.service';
import { ProjectService } from '~core/services/project.service';
import {IItemPrices, IOrderLines, IPrice, PriceSyncStatus, Project} from '~shared/types';
import { ProjectStatus } from '~shared/enums';
import { UserType } from '~shared/enums/user.enum';
import { AlertService } from '~core/services/alert.service';
import { SKIP_NOTIFICATION } from '~core/interceptors/error-handler.interceptor';
import {OrderLineService} from "~core/services/orderline.service";
import {WebsocketService} from "~core/services/websockets.service";
import {AppRepository} from "~shared/store";

import { projectStore } from './projects.store';
import { projectsSelector } from './projects.selectors';

export type AllowedPriceCategory = "PART" | "CUTTINGLIST" | "ITEM";

@Injectable({
	providedIn: 'root',
})
export class ProjectsRepository {
	public projects$ = projectsSelector.projects$;
	public activeProject$ = projectsSelector.activeProject;
	public projectsLoading$ = projectsSelector.projectsLoading$;
	public loadingProjectsState$ = projectsSelector.loadingProjectsState$;
	public loadingProjectsIds$ = projectsSelector.loadingProjectsIds$;

	/**
	 * Whether the active project holds the signed-in user. It mirrors the backend lock
	 * (dk_common project_lock, enforced on every articleservice item write): a locked project
	 * refuses changes from external users only, internal users may still change it.
	 */
	public activeProjectLockedForUser$: Observable<boolean>;

	public lockGlow$ = new Subject<void>();
	private _priceStatus$ = new BehaviorSubject<PriceSyncStatus>('settled');
	/** Whether the prices on screen are final, still being recalculated, or unconfirmed. */
	public priceStatus$ = this._priceStatus$.asObservable();
	private _navigatorCardsLoading$ = new BehaviorSubject<boolean>(false);
	public navigatorCardsLoading$ = this._navigatorCardsLoading$.asObservable();

	public triggerLockGlow(): void {
		this.lockGlow$.next();
	}

	public setPriceStatus(status: PriceSyncStatus): void {
		this._priceStatus$.next(status);
	}

	public setNavigatorCardsLoading(loading: boolean): void {
		this._navigatorCardsLoading$.next(loading);
	}

	public duplicateCompleted$ = new Subject<{ projectId: string; success: boolean }>();

	constructor(
		private readonly projectService: ProjectService,
		private readonly orderLineService: OrderLineService,
		private readonly wsService: WebsocketService,
		private readonly appRepository: AppRepository,
		private readonly alertService: AlertService,
		private readonly authService: AuthService
	) {
		this.activeProjectLockedForUser$ = combineLatest([this.activeProject$, this.authService.currentUser$]).pipe(
			map(([project, user]) => this.isLockedFor(project, user?.userType)),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	/** The same answer as activeProjectLockedForUser$, for a click that needs it now. */
	public isActiveProjectLockedForUser(): boolean {
		return this.isProjectLockedForUser(this.queryActiveProject());
	}

	/** Whether this project, active or not, refuses changes from the signed-in user. */
	public isProjectLockedForUser(project: Project | undefined): boolean {
		return this.isLockedFor(project, this.authService.currentUser$.value?.userType);
	}

	private isLockedFor(project: Project | undefined, userType: UserType | undefined): boolean {
		return !!project?.locked && userType !== UserType.INTERNAL;
	}

	public getProjects$(accountId?: string): Observable<Project[]> {
		projectStore.update(updateRequestStatus('projects', 'pending'));
		return this.projectService.getProjects(accountId)
			.pipe(take(1),
				tap((projects) =>
					projectStore.update(
						setEntities(projects),
						updateRequestStatus('projects', 'success')
					)
				)
			);
	}

	public getProjectsWithPrices$(accountId: string): Observable<Project[]> {
		projectStore.update(updateRequestStatus('projects', 'pending'));
		return this.projectService.getProjects(accountId).pipe(
			take(1),
			tap((projects) => {
				projectStore.update(
					setEntities(projects),
					updateRequestStatus('projects', 'success')
				);
				// Enrich with order lines in the background
				this.appRepository.incrementBackgroundLoading();
				this.orderLineService.getAccountOrderLines(accountId).pipe(
					take(1),
					catchError((error) => {
						console.error('Failed to load account orderlines', error);
						return of([] as IOrderLines[]);
					})
				).subscribe({
					next: (orderLinesList) => {
						const orderLinesMap = new Map<string, IOrderLines>();
						orderLinesList.forEach((ol) => {
							if (ol.projectId) {
								orderLinesMap.set(ol.projectId, ol);
							}
						});
						const enrichedProjects = projects.map((project) => {
							const orderLines = orderLinesMap.get(project.id);
							return orderLines ? { ...project, orderLines } : project;
						});
						projectStore.update(setEntities(enrichedProjects));
						this.appRepository.decrementBackgroundLoading();
					},
					error: () => this.appRepository.decrementBackgroundLoading(),
				});
			})
		);
	}

	public getProject$(projectId: string): Observable<Project | null> {
		const project = projectStore.query((store) => store.entities[projectId]);
		if (project) {
			// Prices come from Odoo and are non-critical: refresh them in the background
			// so the configurator renders immediately even when Odoo is slow/unreachable.
			this.refreshOrderLinesInBackground(projectId);
			return of(project);
		} else {
			projectStore.update(updateRequestStatus(`project-${projectId}`, 'pending'));

			// Prices only need projectId, so fire them now — in parallel with the project
			// fetch, not after it. forkJoin subscribes immediately, so the HTTP request goes
			// out right away even though the merge has to wait for the project entity.
			const orderLines$ = this.orderLineService.getProjectOrderLines(projectId).pipe(take(1));

			// A project that is gone (404) comes back as null without a toast: the caller says
			// where it sends the user instead. Any other failure gets the central toast.
			const project$ = this.projectService.getProject(projectId, new HttpContext().set(SKIP_NOTIFICATION, true)).pipe(
				take(1),
				catchError((error: HttpErrorResponse) => {
					if (error.status === HttpStatusCode.NotFound) {
						return of(null);
					}
					console.error('Failed to load Project', error);
					this.alertService.handleError(error);
					return throwError(() => error);
				}),
				tap((fetchedProject: Project | null) => {
					if (fetchedProject) {
						projectStore.update(
							upsertEntities(fetchedProject),
							updateRequestStatus(`project-${projectId}`, 'success')
						);
					}
				}),
				shareReplay(1)
			);

			// Merge prices once both have arrived. This is off the critical path: the caller
			// receives project$ and renders as soon as the project (not the prices) loads.
			this.appRepository.incrementBackgroundLoading();
			forkJoin([project$, orderLines$]).subscribe({
				next: ([project, orderLines]) => {
					if (project && orderLines) {
						projectStore.update(upsertEntities({ ...project, orderLines }));
					} else if (project && !orderLines) {
						console.warn(`No orderlines found for project ${projectId}`);
					}
					this.appRepository.decrementBackgroundLoading();
				},
				error: () => this.appRepository.decrementBackgroundLoading(),
			});

			return project$;
		}
	}

	/**
	 * Refreshes an already-loaded project's order lines (prices) off the critical path and
	 * merges them into the store once they arrive. A failed/timed-out Odoo call simply leaves
	 * the project without prices, which the UI renders as a question mark.
	 */
	private refreshOrderLinesInBackground(projectId: string): void {
		this.appRepository.incrementBackgroundLoading();
		this.orderLineService.getProjectOrderLines(projectId).pipe(take(1)).subscribe({
			next: (orderLines: IOrderLines) => {
				if (orderLines) {
					const project = this.queryProject(projectId);
					if (project) {
						projectStore.update(upsertEntities({ ...project, orderLines }));
					}
				} else {
					console.warn(`No orderlines found for project ${projectId}`);
				}
				this.appRepository.decrementBackgroundLoading();
			},
			error: () => this.appRepository.decrementBackgroundLoading(),
		});
	}

	public getComponentPrice$(id: string, category: AllowedPriceCategory): Observable<IPrice | undefined> {
		return this.activeProject$.pipe(
			map((project) => {
				if (category === 'ITEM') {
					return project?.itemPrices?.items?.find(
						(item) => item.categoryCode === category && item.productId === id
					)?.price;
				}
				return project?.orderLines?.lines?.find(
					(line) => line.categoryCode === category && line.productId === id
				)?.price;
			})
		);
	}

	public refreshProject$(projectId: string): Observable<Project> {
		return this.projectService.getProject(projectId).pipe(
			tap(project => {
				if (project) {
					projectStore.update(upsertEntities(project));
				}
			}),
		);
	}

	public setProjectItemPrices(projectId: string, itemPrices: IItemPrices): void {
		const project = this.queryProject(projectId);
		if (project) {
			projectStore.update(upsertEntities({ ...project, itemPrices }));
		}
	}

	public setProjectOrderLines(projectId: string, orderLines: IOrderLines): void {
		const project = this.queryProject(projectId);
		if (project) {
			projectStore.update(upsertEntities({ ...project, orderLines }));
		}
	}

	public setActiveProjectId(projectId: string): void {
		if (!projectStore.query(hasEntity(projectId))) {
			throw new Error(`Project with id ${projectId} not found in store, so cannot be set active`);
		}
		projectStore.update(setActiveId(projectId));
	}

	public updateProject$(projectId: string, project: Partial<Project | {name: string, accountId: string}>): Observable<Project> {
		projectStore.update(updateRequestStatus('projects', 'pending'));
		return this.projectService
			.updateProject(projectId, project)
			.pipe(
				tap((project) => {
					projectStore.update(
						upsertEntities(project),
						updateRequestStatus('projects', 'success')
					)
				})
			)
	}

	/** Keeps a colour among the project's wall colours. */
	public addWallColour$(projectId: string, colour: string): Observable<Project> {
		return this.projectService
			.addWallColour(projectId, colour)
			.pipe(tap((project) => projectStore.update(upsertEntities(project))));
	}

	public createProject$(project: Project): Observable<Project> {
		projectStore.update(updateRequestStatus('projects', 'pending'));
		return this.projectService
			.createProject(project)
			.pipe(
				tap((project) => {
					projectStore.update(
						upsertEntities(project),
						updateRequestStatus('projects', 'success')
					)
				})
			)
	}

	public updateProjectStatus$(projectId: string, status: ProjectStatus): Observable<Project> {
		projectStore.update(updateRequestStatus('projects', 'pending'));
		return this.projectService
			.updateProjectStatus(projectId, status)
			.pipe(
				tap((project) => {
					projectStore.update(
						upsertEntities(project),
						updateRequestStatus('projects', 'success')
					)
				})
			)
	}

	/** Archives a project outside the archive and deletes an archived one; the projects page loads the list afterwards. */
	public deleteProject$(projectId: string): Observable<unknown> {
		projectStore.update(updateRequestStatus('projects', 'pending'));

		const knownProject = this.queryProject(projectId);
		const project$ = knownProject ? of(knownProject) : this.projectService.getProject(projectId);

		return project$.pipe(
			switchMap((project) => {
				if (!project) {
					throw new Error(`Project with id ${projectId} not found`);
				}
				return project.status === ProjectStatus.ARCHIVED
					? this.projectService.deleteProject(projectId)
					: this.projectService.updateProjectStatus(projectId, ProjectStatus.ARCHIVED);
			}),
			tap(() => projectStore.update(updateRequestStatus('projects', 'success')))
		);
	}

	/**
	 * Duplicates a project in the background. Because this repository is a root
	 * singleton, the lifecycle (and its toasts) survives navigation, so the user
	 * keeps working and still gets notified once the copy is ready or has failed.
	 */
	public duplicateProjectInBackground(project: Project): void {
		this.alertService.info(
			`Dupliceren project "${project.name}" gestart. U krijgt een melding zodra deze is afgerond en kan ondertussen verder werken.`
		);
		projectStore.update(updateRequestStatus(`project-${project.id}`, 'pending'));

		this.projectService
			.duplicateProject(project.id)
			.pipe(
				mergeMap((duplicated) =>
					this.wsService.getProjectWSMessages$().pipe(
						filter((message) => message?.identifier?.projectId === duplicated.id),
						take(1),
						timeout(10000),
						switchMap(() =>
							this.projectService.getProject(duplicated.id).pipe(
								take(1),
								tap((enrichedProject) => {
									projectStore.update(upsertEntities(enrichedProject));
								})
							)
						)
					)
				),
				take(1)
			)
			.subscribe({
				next: () => {
					this.updateProjectStatus(project.id, 'success');
					this.duplicateCompleted$.next({ projectId: project.id, success: true });
					this.alertService.success(`Dupliceren project "${project.name}" gelukt.`);
				},
				error: (err) => {
					console.error('Duplication failed', err);
					this.updateProjectStatus(project.id, 'error', err);
					this.duplicateCompleted$.next({ projectId: project.id, success: false });
					// A failed call is toasted by the error interceptor; a copy that never reports back is not
					if (!(err instanceof HttpErrorResponse)) {
						this.alertService.error(`Dupliceren project "${project.name}" mislukt.`);
					}
				}
			});
	}

	public clearProjects(): void {
		console.log('clearing projects')
		projectStore.update(
			resetActiveId(),
			deleteAllEntities()
		);
	}

	public resetActiveProject(): void {
		console.log('reset active project')
		projectStore.update(
			resetActiveId(),
		);
	}

	public queryProject(projectId: string): Project | undefined {
		return projectStore.query(getEntity(projectId));
	}

	public queryActiveProject(): Project | undefined {
		const activeProjectId = this.queryActiveProjectId()
		return projectStore.query(getEntity(activeProjectId))
	}

	public queryActiveProjectId(): string | undefined {
		if (!projectStore.query(getActiveId)) {
			console.warn("Active project queried but no active Id set")
			return
		}
		else {
			if (!projectStore.query(hasEntity(projectStore.query(getActiveId)))) {
				console.warn(`Active project queried but no project with active id ${projectStore.query(getActiveId)} found in store`)
				return
			}
		}
		return projectStore.query((store) => store.activeId);
	}

	public reset():void {
		projectStore.reset()
	}

	updateProjectStatus(projectId: string, status: 'success' | 'pending' | 'error', error?: unknown): void {
		if (status === 'error') {
			projectStore.update(updateRequestStatus(`project-${projectId}`, 'error', error));
		} else {
			projectStore.update(updateRequestStatus(`project-${projectId}`, status));
		}
	}

}

export default ProjectsRepository
